import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { PriceHistoryRow, Tariff } from "../types";
import { getPricingHistoryForLocation } from "../../../services/pricemanagment";
import { mapAndSortPriceHistory } from "../utils/tariff";
import { PriceHistoryCard } from "./CommunityPriceCard";
import { isPriceRangeWithinFilterRange, parseISO } from "../utils/format";

type DateRange = [Date | null, Date | null] | null;

const CommunityPriceHistoryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const toastRef = useRef<Toast>(null);
  const [history, setHistory] = useState<PriceHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<DateRange>(null);

  const showError = (msg: string) => {
    toastRef.current?.show({ severity: "error", summary: "Error", detail: msg, life: 4000 });
  };

  const fetchPrices = useCallback(async () => {
    try {
      const data: Tariff[] = await getPricingHistoryForLocation(id!);
      const rows = mapAndSortPriceHistory(data);
      setHistory(rows);
    } catch (e: any) {
      const msg = e?.response?.data?.error ?? "Failed to load community price history.";
      console.error(e);
      showError(msg);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  const filteredHistory = useMemo(() => {
    if (!range || (!range[0] && !range[1])) return history;

    const filterStart = (range[0]?.getTime() ?? Number.NEGATIVE_INFINITY);
    const filterEnd = (range[1]?.getTime() ?? Number.POSITIVE_INFINITY);

    return history.filter(h => {
      const priceStart = parseISO(h.active_from) ?? Number.NEGATIVE_INFINITY;
      const priceEnd = parseISO(h.active_till) ?? Number.POSITIVE_INFINITY;
      return isPriceRangeWithinFilterRange(priceStart, priceEnd, filterStart, filterEnd);
    });
  }, [history, range]);

  const clearRange = () => setRange(null);

  if (loading) {
    return (
      <div className="loading-container">
        <ProgressSpinner />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <>
        <Toast ref={toastRef} position="top-right" />
        <div className="p-4 text-700">No history found for station <b>{id}</b>.</div>
      </>
    );
  }

  return (
    <>
      <Toast ref={toastRef} position="top-right" />

      <div className="flex align-items-center justify-content-between">
        <h1 className="text-blue-800 mb-3">Price History for {id}</h1>
        {/* Filter bar */}
        <div className="flex align-items-end gap-2 mb-4">
          <span className="flex flex-column">
            <label htmlFor="range" className="text-md text-600 mb-2 px-2">Active during:</label>
            <Calendar
              id="range"
              value={range as any}
              onChange={(e) => setRange(e.value as DateRange)}
              selectionMode="range"
              showIcon
              showTime
              hourFormat="12"
              placeholder="Pick date & time range"
              className="w-full"
            />
          </span>
          <Button label="Clear" onClick={clearRange} outlined />
        </div>
      </div>

      {/* Results */}
      {filteredHistory.length === 0 ? (
        <div className="p-4 text-700">No tariffs active in the selected period.</div>
      ) : (
        <div className="grid">
          {filteredHistory.map((h, idx) => (
            <div key={`${h.tariff_id}-${h.active_from || idx}`} className="col-12 md:col-6">
              <PriceHistoryCard item={h} />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default CommunityPriceHistoryPage;
